import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Archive, FileText, CaretDown } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";

export const AlwaysIncludeIndicator = ({ onFilesLoaded }) => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const fetchAlwaysInclude = async () => {
      try {
        const resp = await api.get("/resource-bank/always-include");
        const list = resp.data || [];
        setFiles(list);
        onFilesLoaded?.(list);
      } catch {
        // silent — non-critical
      }
    };
    fetchAlwaysInclude();
  }, [api, onFilesLoaded]);

  if (files.length === 0) return null;

  return (
    <div className="space-y-0" data-testid="always-include-indicator">
      <button
        type="button"
        onClick={() => setExpanded((p) => !p)}
        className="inline-flex items-center gap-2 bg-[var(--bg-raised)] border border-[var(--border)] rounded-full px-3 py-1.5 text-xs text-[var(--text-muted)] hover:text-[var(--text-secondary)] transition-colors"
        data-testid="always-include-pill"
      >
        <Archive size={14} weight="fill" />
        {files.length} resource file{files.length !== 1 ? "s" : ""} will be included
        <CaretDown size={12} className={`transition-transform duration-200 ${expanded ? "rotate-180" : ""}`} />
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="mt-2 rounded-lg bg-[var(--bg-base)] border border-[var(--border)] p-3 space-y-1" data-testid="always-include-list">
              {files.map((f) => (
                <div key={f.resource_id} className="flex items-center gap-2">
                  <FileText size={14} className="text-[var(--text-muted)] shrink-0" />
                  <span className="text-xs text-[var(--text-secondary)] truncate">{f.display_name}</span>
                </div>
              ))}
              <button
                type="button"
                onClick={() => navigate("/settings?tab=resources")}
                className="text-xs text-[#FF4400] hover:text-[#FF6633] mt-1.5 transition-colors"
                data-testid="manage-resources-link"
              >
                Manage in Settings &rarr;
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
